// The public route table as data (ARCHITECTURE §4.1, §6.3). Templates never
// declare routes: SiteTemplateHost maps SITE_ROUTES onto the active
// template's page views; App.tsx mounts STANDALONE_ROUTES (core pages that
// look the same in every template) beside the admin routes.
//
// Order is today's App.tsx order. test/site/contract/routes.parity.test.ts
// checks the union of these tables and the admin routes against a snapshot of
// the pre-templates App.tsx, gates included, so a route can't be lost.

import type { AddonKey, SiteSectionKey, StrictAddonKey } from '@/contexts/tenantAccess'
import type { SitePageKey } from './template/pages'

export type { SitePageKey } from './template/pages'

export interface SiteRouteDef {
  path: string
  page: SitePageKey
  /** Wraps in <SectionRoute section=…>. */
  section?: SiteSectionKey
  /** Wraps in <AddonRoute addon=…>. */
  addon?: AddonKey
  /**
   * Mounted only while this strict add-on is entitled; otherwise the URL
   * falls through to the catch-all exactly as if the route did not exist (no
   * redirect, fail-closed while the tenant is unknown). Unlike `addon`, which
   * fails open and redirects to '/'. SiteTemplateHost filters on it.
   */
  onlyWith?: StrictAddonKey
  /**
   * 'open': mirrors the backend's openOnPrivateSite() for the site's own
   * pages (registration step 33). On a members-only league these routes
   * render for a visitor who has not been admitted (sign-in, Register, Join,
   * My account, password recovery); every other route renders the sign-in
   * wall in place (SiteTemplateHost). No effect on a public league. These are
   * all `onlyWith` registration routes, so a lapsed add-on removes them and
   * the wall's closed state is all that shows (fail closed).
   */
  privateSite?: 'open'
  /**
   * With `onlyWith` not entitled and the tenant known, the route still
   * mounts, on this page instead of falling through to not found (owner
   * audit, October 29): the member pages a Stripe return or an emailed link
   * can land on while the registration add-on is off show "Registration
   * isn't available right now" (the registrations controller's off state).
   * It is not `privateSite: 'open'` then, so a members-only league still
   * walls it. The gate (and route parity) is unchanged.
   */
  whenOff?: SitePageKey
}

// 1.0's form pages (/forms/:slug and /forms/:slug/success) were retired at
// registration step R (decision 25): both paths fall to each template's
// not-found page.
export const SITE_ROUTES: readonly SiteRouteDef[] = [
  { path: '/', page: 'home' },
  { path: '/schedule', page: 'schedule', section: 'schedule' },
  { path: '/schedule/:id', page: 'schedule', section: 'schedule' },
  { path: '/players', page: 'players', section: 'players' },
  { path: '/players/:id', page: 'player', section: 'players' },
  { path: '/teams', page: 'teams', section: 'teams' },
  { path: '/teams/:id', page: 'team', section: 'teams' },
  { path: '/stats', page: 'stats', section: 'stats' },
  { path: '/game/:id', page: 'game' },
  { path: '/blog', page: 'blog', section: 'blog' },
  { path: '/gallery', page: 'gallery', section: 'gallery' },
  { path: '/pages/:slug', page: 'page' },
  { path: '/live', page: 'liveEvents', addon: 'broadcast' },
  { path: '/live/:slug', page: 'liveEvent', addon: 'broadcast' },
  { path: '/sponsor/:id', page: 'sponsor' },
  { path: '/venues', page: 'venues', section: 'venues' },
  { path: '/venues/:id', page: 'venue', section: 'venues' },
  { path: '/tickets', page: 'tickets', addon: 'ticketing' },
  { path: '/tickets/:slug', page: 'sale', addon: 'ticketing' },
  // Registration plan step 2 (lead decision, plan v4.2 §8/§15, open question
  // 4): member sign-in / sign-up. Not /auth/login or /auth/register
  // (security-model.md quirk 53). Leagues without the add-on route exactly as
  // before: /sign-in is an unknown URL for them.
  { path: '/sign-in', page: 'signIn', onlyWith: 'registration', privateSite: 'open' },
  // Registration plan step 6: the public Register list and one
  // registration's page. Strict add-on (onlyWith); without it (tenant known)
  // both show the off page (whenOff, October 29). These are frontend paths; they call only
  // /api/v1/member/registrations* (security-model.md quirk 53 concerns
  // backend paths containing /auth/register, which these never are).
  { path: '/register', page: 'registrations', onlyWith: 'registration', privateSite: 'open', whenOff: 'registrations' },
  // Registration plan step 16: the invitation page (player and individual
  // invitations; the link is member-links.ts › memberInviteUrl). 'join' is in
  // RESERVED_SLUGS (backend registrations.ts), so no registration can take
  // the slug, and the static segment outranks :slug. The token travels in
  // ?invite_token= (log-redact covers query params named *token*). Without
  // the add-on (tenant known) it shows the off page (whenOff, October 29).
  { path: '/register/join', page: 'join', onlyWith: 'registration', privateSite: 'open', whenOff: 'registrations' },
  { path: '/register/:slug', page: 'registration', onlyWith: 'registration', privateSite: 'open', whenOff: 'registrations' },
  // Registration plan step 9: My account and password recovery. Strict
  // add-on (onlyWith): without it /forgot-password routes as before, and
  // /account and the reset pages an emailed link lands on (QA, October 30)
  // show the off page (whenOff, October 29). None
  // of these paths contains /auth/login or /auth/register (security-model.md
  // quirk 53). They are deliberately in neither sport-switcher hidden list
  // (site-templates.md quirk 44). /reset-password (no token) is where a
  // refresh or Back lands once the page has stripped the token from the
  // address bar: it shows the expired view (the staff /admin/reset-password
  // likewise reads a missing token as a link to ask for again).
  { path: '/account', page: 'account', onlyWith: 'registration', privateSite: 'open', whenOff: 'registrations' },
  { path: '/forgot-password', page: 'forgotPassword', onlyWith: 'registration', privateSite: 'open' },
  { path: '/reset-password', page: 'resetPassword', onlyWith: 'registration', privateSite: 'open', whenOff: 'registrations' },
  { path: '/reset-password/:token', page: 'resetPassword', onlyWith: 'registration', privateSite: 'open', whenOff: 'registrations' },
]

/** Template-independent core pages (D7). Rendered by App.tsx, never by a template. */
export type StandalonePageKey = 'ticket' | 'orderWallet' | 'purchaseSuccess' | 'scoreBugOverlay'

export interface StandaloneRouteDef {
  path: string
  page: StandalonePageKey
  addon?: AddonKey
  /** OBS: skips the tenant gates (App.tsx renders it even with no league). */
  tenantAgnostic?: boolean
}

export const STANDALONE_ROUTES: readonly StandaloneRouteDef[] = [
  { path: '/tickets/t/:token', page: 'ticket', addon: 'ticketing' },
  { path: '/tickets/order/:token', page: 'orderWallet', addon: 'ticketing' },
  { path: '/tickets/success', page: 'purchaseSuccess', addon: 'ticketing' },
  { path: '/obs/scorebug/:uuid', page: 'scoreBugOverlay', tenantAgnostic: true },
]

/** Admin-v2 routes (out of the template system; listed for the parity test). */
export const ADMIN_ROUTE_PATHS: readonly string[] = ['/admin/login', '/admin/impersonate', '/admin/*']
